/**
 * Generation assignment — docs/08 §4.2 step 1.
 *
 * Generation is not a property of a person. It is the solution to a system of
 * *difference* constraints between them: a child sits exactly one band below
 * each parent (`g(child) − g(parent) = 1`), and spouses and siblings share a
 * band (`g(a) − g(b) = 0`). Written that way the whole problem is a weighted
 * union-find — each person carries an offset to their component's root — which
 * is exact, linear, deterministic, and detects a genuine contradiction instead
 * of drifting towards one.
 *
 * The earlier version layered by longest path from the top and treated marriages
 * as a separate pull afterwards. That fails on the ordinary raggedness of real
 * trees: one branch documented a generation deeper than the branch it married
 * into pushed its whole line down, and then *every* marriage across the two read
 * as a violated constraint. Anchoring people to each other rather than to the
 * top of the chart is what makes a marriage align two branches, which is exactly
 * what a marriage does in life.
 *
 * Priority is the order constraints are applied: bloodlines first, so they can
 * never be overruled; then marriages and siblinghood; then the loose
 * "below, somewhere" of fostering and guardianship. A constraint that
 * contradicts what is already settled is recorded as a violation and skipped —
 * the chart routes those through the band gutters (§4.5) rather than pretending.
 *
 * `step` rows never appear here: a step-parent's band comes from their union
 * with the real parent, and layering through the step edge would push their
 * step-children below the family they belong to.
 */

import type { TreeEdge } from '@famtree/shared';

/** Bases that place a child exactly one band below the parent. */
const LAYERING_BASES = new Set(['biological', 'adoptive', 'unknown']);

/** Bases that say "below, somewhere" without fixing the distance. */
const SOFT_DESCENT_BASES = new Set(['foster', 'guardianship']);

export interface Layering {
  generation: Map<string, number>;
  /** Ids of edges the solution could not satisfy — routed specially (§4.5). */
  violations: string[];
}

/**
 * Union-find over difference constraints: `offset[x]` is `g(x) − g(root(x))`.
 * Path compression accumulates the offsets as it goes, so a find is still
 * near-constant and the offsets stay exact.
 */
class DifferenceSet {
  private readonly parent = new Map<string, string>();
  private readonly offset = new Map<string, number>();

  add(id: string): void {
    if (this.parent.has(id)) return;
    this.parent.set(id, id);
    this.offset.set(id, 0);
  }

  /** The component root, and this element's generation relative to it. */
  find(id: string): { root: string; offset: number } {
    const parent = this.parent.get(id);
    if (parent === undefined || parent === id) return { root: id, offset: 0 };
    const up = this.find(parent);
    const total = this.offset.get(id)! + up.offset;
    this.parent.set(id, up.root);
    this.offset.set(id, total);
    return { root: up.root, offset: total };
  }

  /**
   * Assert `g(a) − g(b) = delta`. Returns false when the two are already related
   * by a different difference — a real contradiction, not a preference.
   */
  constrain(a: string, b: string, delta: number): boolean {
    this.add(a);
    this.add(b);
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra.root === rb.root) return ra.offset - rb.offset === delta;
    // Hang a's root under b's: g(ra.root) = g(b) + delta − ra.offset.
    this.parent.set(ra.root, rb.root);
    this.offset.set(ra.root, rb.offset + delta - ra.offset);
    return true;
  }

  generationOf(id: string): { root: string; offset: number } {
    this.add(id);
    return this.find(id);
  }
}

export function assignGenerations(personIds: string[], edges: TreeEdge[]): Layering {
  const solver = new DifferenceSet();
  for (const id of personIds) solver.add(id);
  const present = new Set(personIds);
  const violations: string[] = [];

  const hard: TreeEdge[] = [];
  const softDescent: TreeEdge[] = [];
  const sameBand: TreeEdge[] = [];

  for (const edge of edges) {
    if (!present.has(edge.a) || !present.has(edge.b)) continue;
    if (edge.kind === 'parent_child') {
      const basis = edge.parent_basis ?? 'unknown';
      if (basis === 'step') continue; // never drawn, never layered (§4.3)
      if (LAYERING_BASES.has(basis)) hard.push(edge);
      else if (SOFT_DESCENT_BASES.has(basis)) softDescent.push(edge);
    } else {
      sameBand.push(edge);
    }
  }

  // Deterministic order: the same family must always produce the same chart, and
  // *which* constraint loses a contradiction depends on which was applied first.
  const byId = (x: TreeEdge, y: TreeEdge): number => x.id.localeCompare(y.id);

  // 1 — bloodlines. These never yield; a contradiction here is bad data.
  for (const edge of [...hard].sort(byId)) {
    if (!solver.constrain(edge.b, edge.a, 1)) violations.push(edge.id);
  }
  // 2 — marriages and siblinghood: same band, unless the bloodlines already
  //     placed the two people a fixed distance apart.
  for (const edge of [...sameBand].sort(byId)) {
    if (!solver.constrain(edge.a, edge.b, 0)) violations.push(edge.id);
  }
  // 3 — fostering and guardianship: below, but the distance is not a claim, so
  //     they are only pinned when nothing else has spoken for that pair.
  for (const edge of [...softDescent].sort(byId)) {
    const child = solver.generationOf(edge.b);
    const parent = solver.generationOf(edge.a);
    if (child.root === parent.root) {
      if (child.offset <= parent.offset) violations.push(edge.id);
      continue;
    }
    solver.constrain(edge.b, edge.a, 1);
  }

  /*
   * Components are each internally consistent but float relative to one another
   * — a family with no recorded link to the rest of the tree has no absolute
   * generation. Each is placed so its own topmost person sits at 0, and the
   * whole chart is normalised afterwards; ordering components by their root id
   * keeps that placement stable across runs.
   */
  const byRoot = new Map<string, Array<{ id: string; offset: number }>>();
  for (const id of personIds) {
    const { root, offset } = solver.generationOf(id);
    byRoot.set(root, [...(byRoot.get(root) ?? []), { id, offset }]);
  }

  const generation = new Map<string, number>();
  for (const members of [...byRoot.entries()].sort((x, y) => x[0].localeCompare(y[0]))) {
    const offsets = members[1].map((member) => member.offset);
    const top = Math.min(...offsets);
    for (const member of members[1]) generation.set(member.id, member.offset - top);
  }

  return { generation, violations };
}
